import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import {
  EGuideDetailSortField,
  IGuideDetail,
  PGuideDetailFilter,
} from "../entities";
import IGuideDetailRepository from "../repository";

export default class GuideDetailRepository
  implements IGuideDetailRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private GUIDE_DETAIL_ROUTE = "/admin/guide-detail";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PGuideDetailFilter) => {
    return filter;
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getGuideDetail(
    pagination: IPagination,
    filter: PGuideDetailFilter,
    sorts: ISort<EGuideDetailSortField>[],
    signal?: CancelTokenSource
  ): Promise<[IGuideDetail[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      Boolean
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.GUIDE_DETAIL_ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getGuideDetailById(id: number): Promise<IGuideDetail> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.GUIDE_DETAIL_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    const guideDetail = response.data as IGuideDetail;
    guideDetail.detailImagePaths = guideDetail.detail_image_paths
      ? guideDetail.detail_image_paths.split(",")
      : [];
    return guideDetail;
  }

  async createGuideDetail(formData: FormData): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(this.GUIDE_DETAIL_ROUTE, formData)
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async updateGuideDetail(id: number, formData: FormData): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().patch(
        `${this.GUIDE_DETAIL_ROUTE}/${id}`,
        formData
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async deleteGuideDetail(id: number): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().delete(`${this.GUIDE_DETAIL_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
